import { z } from 'zod';
import { DomainError } from '../errors.js';
import { addMonths, compareLocalDate, type LocalDate } from '../local-date.js';
import { toIssues } from '../materials/catalog.js';
import type { ParseResult } from '../voyage/schemas.js';
import { DRILL_PLAN_STATES, type DrillPlanState } from './engine.js';
import { DEFAULT_DRILL_RULES, DRILL_RULE_CODES } from './rules.js';
import { drillContentSchema } from './template.js';

/**
 * Tatbikat planının girdi şemaları ve kuralları (ADR 0128 Faz 2 Ek; belge §4). Plan, kayıtlı
 * uygulama, erteleme ve kaptan kararı gemi verisidir; şirket kural satırı ve varyant kararı
 * ofisindir. Şemalar gemi, bulut ve arayüzde ortaktır.
 */

const missing = (input: unknown) => input === undefined || input === null;
const typed = (code: string) => ({
  error: (issue: { input?: unknown }) => (missing(issue.input) ? 'REQUIRED' : code),
});
const collapse = (value: string) => value.normalize('NFC').trim().replace(/\s+/g, ' ');
const paragraph = (value: string) =>
  value
    .normalize('NFC')
    .split(/\r\n|\r|\n/)
    .map((line) => line.trim().replace(/[ \t]+/g, ' '))
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
const optionalText = (max: number) =>
  z
    .string(typed('INVALID_TEXT'))
    .transform(collapse)
    .pipe(z.string().max(max, 'TOO_LONG'))
    .transform((value) => (value === '' ? null : value))
    .nullish()
    .transform((value) => value ?? null);
const requiredParagraph = (max: number) =>
  z
    .string(typed('INVALID_TEXT'))
    .transform(paragraph)
    .pipe(z.string().min(1, 'REQUIRED').max(max, 'TOO_LONG'));
const optionalParagraph = (max: number) =>
  z
    .string(typed('INVALID_TEXT'))
    .transform(paragraph)
    .pipe(z.string().max(max, 'TOO_LONG'))
    .transform((value) => (value === '' ? null : value))
    .nullish()
    .transform((value) => value ?? null);
/** Boşlar atılır, büyük/küçük harf duyarsız kopyalar teke iner (ilk yazılış kalır). */
const textList = (maxItems: number, maxLength: number) =>
  z
    .array(z.string(typed('INVALID_TEXT')), typed('INVALID_LIST'))
    .nullish()
    .transform((items) => {
      const seen = new Set<string>();
      const out: string[] = [];
      for (const item of (items ?? []).map(collapse)) {
        const key = item.toLowerCase();
        if (item === '' || seen.has(key)) continue;
        seen.add(key);
        out.push(item);
      }
      return out;
    })
    .pipe(z.array(z.string().max(maxLength, 'TOO_LONG')).max(maxItems, 'TOO_MANY'));
const uuid = z.uuid('INVALID_ID');
const nullableUuid = uuid.nullish().transform((value) => value ?? null);
const DAY = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
const day = z
  .string(typed('INVALID_DATE'))
  .regex(DAY, 'INVALID_DATE')
  .refine((value) => {
    const date = new Date(`${value}T00:00:00Z`);
    return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
  }, 'INVALID_DATE');
const months = z
  .number(typed('INVALID_NUMBER'))
  .int('INVALID_NUMBER')
  .min(1, 'OUT_OF_RANGE')
  .max(60, 'OUT_OF_RANGE');

function parsed<T>(result: z.ZodSafeParseResult<T>): ParseResult<T> {
  return result.success
    ? { ok: true, value: result.data }
    : {
        ok: false,
        issues: toIssues(result.error).filter(
          (issue, index, all) => all.findIndex((i) => i.path === issue.path) === index,
        ),
      };
}

/* ---------- Plan satırı ---------- */

export const drillPlanSchema = z.strictObject({
  /** Master Library'den seçilen şablon (belge §4 "Drill"). */
  templateId: uuid,
  /** Responsible Officer: gemideki kişi (belge §4; yetki ve gemi personelinden). */
  responsibleUserId: nullableUuid,
  /** Participants: rollere göre (belge §4). */
  participantRoles: textList(40, 80),
  /**
   * Planın kendi aralığı (ay). Boş: kuralın (şirket kural seti) ya da şablonun sıklığı. Kurala bağlı
   * planda kuraldan uzun olamaz (`INTERVAL_EXCEEDS_RULE`; repository denetler).
   */
  intervalMonths: months.nullish().transform((value) => value ?? null),
  /** Hiç onaylı uygulama yokken ilk vade. */
  startOn: day,
  notes: optionalParagraph(2000),
  /** Taslak ya da yürürlükte; iptal ayrı işlemdir (gerekçeli). */
  state: z
    .enum(['draft', 'active'], typed('INVALID_OPTION'))
    .nullish()
    .transform((value): Exclude<DrillPlanState, 'cancelled'> => value ?? 'active'),
});
export type DrillPlanInput = z.output<typeof drillPlanSchema>;

export function parseDrillPlan(input: unknown): ParseResult<DrillPlanInput> {
  return parsed(drillPlanSchema.safeParse(input));
}

export const drillPlanCancelSchema = z.strictObject({ reason: requiredParagraph(2000) });

export function parseDrillPlanCancel(input: unknown): ParseResult<{ reason: string }> {
  return parsed(drillPlanCancelSchema.safeParse(input));
}

export function isDrillPlanState(value: unknown): value is DrillPlanState {
  return (DRILL_PLAN_STATES as readonly unknown[]).includes(value);
}

/* ---------- Kayıtlı uygulama ---------- */

/**
 * `baseline`: ilk kurulumda kaptanın girdiği son uygulama (gerekçeli; ADR 0128 §1); `recorded`:
 * Faz 3'ün icra kaydı gelene kadar elle girilen uygulama (eski ISM tatbikat formu bağı isteğe
 * bağlı). İkisi de kaptan onayıyla Last Conducted olur.
 */
export const DRILL_CONDUCT_KINDS = ['baseline', 'recorded'] as const;
export type DrillConductKind = (typeof DRILL_CONDUCT_KINDS)[number];

export const drillConductSchema = z
  .strictObject({
    planId: uuid,
    conductedOn: day,
    kind: z.enum(DRILL_CONDUCT_KINDS, typed('INVALID_OPTION')),
    /** Eski kayıt numarası ya da referans (ör. ISM formunun numarası). */
    reference: optionalText(120),
    /** Eski ISM tatbikat formu kaydı (`form_records`); isteğe bağlı. */
    formRecordId: nullableUuid,
    notes: optionalParagraph(2000),
  })
  .superRefine((value, ctx) => {
    if (value.kind === 'baseline' && value.notes === null) {
      ctx.addIssue({ code: 'custom', path: ['notes'], message: 'REQUIRED' });
    }
  });
export type DrillConductInput = z.output<typeof drillConductSchema>;

export function parseDrillConduct(input: unknown): ParseResult<DrillConductInput> {
  return parsed(drillConductSchema.safeParse(input));
}

/**
 * Kayıtlı uygulama kuralları: gelecek tarih yok ("gelecek tarih" gemi saatine göre; CLAUDE.md
 * "Zaman"), aynı günde ikinci uygulama yok (onaylı ya da bekleyen), kaptan onayı bekleyen varken
 * yenisi girilmez.
 */
export function assertDrillConduct(input: {
  conductedOn: LocalDate;
  today: LocalDate;
  /** Planın onaylı ve bekleyen uygulamalarının günleri. */
  existing: readonly LocalDate[];
  pending: boolean;
}): void {
  if (compareLocalDate(input.conductedOn, input.today) > 0) {
    throw new DomainError('FUTURE_DATE', input.conductedOn);
  }
  if (input.pending) throw new DomainError('CONDUCT_PENDING');
  if (input.existing.includes(input.conductedOn)) {
    throw new DomainError('DUPLICATE_CONDUCT_DATE', input.conductedOn);
  }
}

/* ---------- Erteleme ---------- */

export const drillPostponementSchema = z.strictObject({
  planId: uuid,
  newDueOn: day,
  reason: requiredParagraph(2000),
});
export type DrillPostponementInput = z.output<typeof drillPostponementSchema>;

export function parseDrillPostponement(input: unknown): ParseResult<DrillPostponementInput> {
  return parsed(drillPostponementSchema.safeParse(input));
}

/**
 * Erteleme kuralları (belge §4, §13.2; ADR 0108 kalıbı): yalnız yürürlükteki plan; kaptan kararı
 * bekleyen varken ikincisi yok; yeni vade önceki vadeden sonra ve bugünden önce değil; yeni vade
 * orijinal vade + bir aralığı geçmez (sonraki döngüyü atlatmaz; sıkı seçenek, ADR 0128 Faz 2 Ek).
 */
export function assertDrillPostponement(input: {
  state: DrillPlanState;
  pending: boolean;
  previousDueOn: LocalDate;
  originalDueOn: LocalDate;
  newDueOn: LocalDate;
  intervalMonths: number;
  today: LocalDate;
}): void {
  if (input.state !== 'active') throw new DomainError('DRILL_PLAN_NOT_ACTIVE', input.state);
  if (input.pending) throw new DomainError('POSTPONEMENT_PENDING');
  if (compareLocalDate(input.newDueOn, input.previousDueOn) <= 0) {
    throw new DomainError('POSTPONEMENT_MUST_BE_LATER', input.newDueOn);
  }
  if (compareLocalDate(input.newDueOn, input.today) < 0) {
    throw new DomainError('DUE_IN_PAST', input.newDueOn);
  }
  if (compareLocalDate(input.newDueOn, addMonths(input.originalDueOn, input.intervalMonths)) > 0) {
    throw new DomainError('POSTPONEMENT_TOO_LONG', input.newDueOn);
  }
}

/* ---------- Kaptan kararı ---------- */

/**
 * Kararın konusu: erteleme, kayıtlı uygulama ve (Faz 3) icra kaydının gönderimi
 * (`drill_record_events` `submitted` satırı; ADR 0128 Faz 3 Ek).
 */
export const DRILL_DECISION_SUBJECTS = ['postponement', 'conduct', 'record'] as const;
export type DrillDecisionSubject = (typeof DRILL_DECISION_SUBJECTS)[number];

export const drillDecisionSchema = z
  .strictObject({
    subject: z.enum(DRILL_DECISION_SUBJECTS, typed('INVALID_OPTION')),
    id: uuid,
    decision: z.enum(['approved', 'returned'], typed('INVALID_OPTION')),
    reason: optionalParagraph(2000),
    /** Kaptanın gördüğü içeriğin hash'i (CLAUDE.md "Onay içerik hash'ini taşır"). */
    contentHash: z.string(typed('INVALID_TEXT')).min(1, 'REQUIRED'),
  })
  .superRefine((value, ctx) => {
    if (value.decision === 'returned' && value.reason === null) {
      ctx.addIssue({ code: 'custom', path: ['reason'], message: 'REQUIRED' });
    }
  });
export type DrillDecisionInput = z.output<typeof drillDecisionSchema>;

export function parseDrillDecision(input: unknown): ParseResult<DrillDecisionInput> {
  return parsed(drillDecisionSchema.safeParse(input));
}

/* ---------- Şirket kural satırı ---------- */

export const companyDrillRuleSchema = z
  .strictObject({
    code: z
      .string(typed('INVALID_TEXT'))
      .refine((value) => DRILL_RULE_CODES.includes(value), 'INVALID_OPTION'),
    vesselTypes: textList(20, 60),
    flags: textList(20, 60),
    intervalMonths: months.nullish().transform((value) => value ?? null),
    triggerHours: z
      .number(typed('INVALID_NUMBER'))
      .int('INVALID_NUMBER')
      .min(1, 'OUT_OF_RANGE')
      .max(720, 'OUT_OF_RANGE')
      .nullish()
      .transform((value) => value ?? null),
    applicable: z
      .boolean(typed('INVALID_BOOLEAN'))
      .nullish()
      .transform((value) => value ?? true),
    /** Gerekçe (SMS bölümü, bayrak genelgesi, yetkili kişinin kararı); denetim izinde görünür. */
    reason: requiredParagraph(1000),
    active: z
      .boolean(typed('INVALID_BOOLEAN'))
      .nullish()
      .transform((value) => value ?? true),
  })
  .superRefine((value, ctx) => {
    const base = DEFAULT_DRILL_RULES.find((r) => r.code === value.code);
    if (!base || !value.applicable) return;
    if (base.intervalMonths !== null && value.intervalMonths === null) {
      ctx.addIssue({ code: 'custom', path: ['intervalMonths'], message: 'REQUIRED' });
    }
    if (base.triggerHours !== null && value.triggerHours === null) {
      ctx.addIssue({ code: 'custom', path: ['triggerHours'], message: 'REQUIRED' });
    }
    // Mevzuat kuralı (SOLAS, ISPS) şirketçe yalnız sıkılaştırılır; muafiyet (bayrak, gemi tipi)
    // "uygulanmaz" satırıyla ve gerekçeyle yazılır. Şirket SMS kuralı serbesttir (ADR 0128 Faz 2 Ek)
    if (base.basis !== 'regulation') return;
    if (
      base.intervalMonths !== null &&
      value.intervalMonths !== null &&
      value.intervalMonths > base.intervalMonths
    ) {
      ctx.addIssue({ code: 'custom', path: ['intervalMonths'], message: 'LOOSER_THAN_REGULATION' });
    }
    if (
      base.triggerHours !== null &&
      value.triggerHours !== null &&
      value.triggerHours > base.triggerHours
    ) {
      ctx.addIssue({ code: 'custom', path: ['triggerHours'], message: 'LOOSER_THAN_REGULATION' });
    }
  })
  .transform((value) => {
    const base = DEFAULT_DRILL_RULES.find((r) => r.code === value.code);
    const periodic = base?.intervalMonths !== null;
    // Kuralın türüne ait olmayan değer tutulmaz; uygulanmaz satırda değer yoktur
    return {
      ...value,
      intervalMonths: value.applicable && periodic ? value.intervalMonths : null,
      triggerHours: value.applicable && !periodic ? value.triggerHours : null,
    };
  });
export type CompanyDrillRuleInput = z.output<typeof companyDrillRuleSchema>;

export function parseCompanyDrillRule(input: unknown): ParseResult<CompanyDrillRuleInput> {
  return parsed(companyDrillRuleSchema.safeParse(input));
}

/* ---------- Gemiye özel varyant ---------- */

/**
 * Gemiye özel senaryo (belge "Merkezi Master Library + gemiye özel senaryo + ofis onayı"; §13.5):
 * şablonun içeriğinin gemiye uyarlanmış hâli. Merkez şablon değişmez; ofis onayıyla gemide geçerli
 * olur.
 */
export const drillVariantSchema = z.strictObject({
  templateId: uuid,
  content: drillContentSchema,
  reason: requiredParagraph(2000),
});
export type DrillVariantInput = z.output<typeof drillVariantSchema>;

export function parseDrillVariant(input: unknown): ParseResult<DrillVariantInput> {
  return parsed(drillVariantSchema.safeParse(input));
}

export const drillVariantDecisionSchema = z
  .strictObject({
    decision: z.enum(['approved', 'returned'], typed('INVALID_OPTION')),
    reason: optionalParagraph(2000),
    contentHash: z.string(typed('INVALID_TEXT')).min(1, 'REQUIRED'),
  })
  .superRefine((value, ctx) => {
    if (value.decision === 'returned' && value.reason === null) {
      ctx.addIssue({ code: 'custom', path: ['reason'], message: 'REQUIRED' });
    }
  });
export type DrillVariantDecisionInput = z.output<typeof drillVariantDecisionSchema>;

export function parseDrillVariantDecision(input: unknown): ParseResult<DrillVariantDecisionInput> {
  return parsed(drillVariantDecisionSchema.safeParse(input));
}
